import { type VariantProps, cva } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"
import { forwardRef, type ButtonHTMLAttributes } from "react"
import { cn } from "@/shared/utils/general/cn"

/**
 * Button — matches the v1 MUI Button with custom variants.
 *
 * Variants ported from v1:
 *   primary   → solid primary color (pink by default)
 *   stroke    → outlined with primary border
 *   secondary → lighter primary tint
 *   ghost     → transparent bg, primary text
 *   error     → solid red
 *   warning   → solid yellow
 *   success   → solid green
 *
 * Sizes ported from v1:
 *   large      → 12px 24px padding, 12px radius
 *   default    → 7px 24px padding, 10px radius (v1 root default)
 *   small      → 6px 16px padding, 10px radius, 14px font
 *   extraSmall → 4px 12px padding, 6px radius, 12px font
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-semibold transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-primary text-primary-foreground",
          "hover:bg-primary/90",
          "active:bg-primary/80",
          "disabled:bg-primary/38 disabled:text-primary-foreground/38",
        ].join(" "),
        stroke: [
          "border border-primary bg-transparent text-primary",
          "hover:bg-primary/8",
          "active:bg-primary/16",
          "disabled:border-primary/38 disabled:text-primary/38",
        ].join(" "),
        secondary: [
          "bg-secondary text-secondary-foreground",
          "hover:bg-secondary/80",
          "disabled:bg-secondary/38 disabled:text-secondary-foreground/38",
        ].join(" "),
        ghost: [
          "bg-transparent text-primary",
          "hover:bg-primary/8",
          "disabled:text-primary/38",
        ].join(" "),
        error: [
          "bg-[var(--red-500)] text-white",
          "hover:bg-[var(--red-700)]",
          "disabled:bg-[var(--red-500)]/38 disabled:text-white/38",
        ].join(" "),
        warning: [
          "bg-warning text-warning-foreground",
          "hover:bg-[var(--yellow-700)]",
          "disabled:bg-warning/38 disabled:text-warning-foreground/38",
        ].join(" "),
        success: [
          "bg-success text-success-foreground",
          "hover:bg-[var(--green-700)]",
          "disabled:bg-success/38 disabled:text-success-foreground/38",
        ].join(" "),
        // shadcn defaults for compatibility
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        large: "h-12 rounded-xl px-6 py-3 text-base",
        default: "h-10 rounded-[10px] px-6 py-[7px] text-sm",
        small: "h-8 rounded-[10px] px-4 py-1.5 text-sm leading-[22px]",
        extraSmall: "h-6 rounded-md px-3 py-1 text-xs leading-[18px]",
        icon: "h-10 w-10 rounded-[10px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
